import { Component, EventEmitter, Input, Output } from '@angular/core';

export type DocumentAttachmentCardVariant = 'default' | 'compact' | 'message';
export type DocumentAttachmentCardState = 'completed' | 'uploading' | 'failed' | 'cancelled' | 'interrupted';

/**
 * Tarjeta reutilizable para cualquier adjunto de chat sin preview visual real
 * (PDF, DOCX, XLSX, ZIP, JSON, binarios, etc). Puramente presentacional: no
 * conoce la clasificación de tipo de archivo ni el mecanismo de descarga —
 * el caller (que sí importa `lib-klab-core`) resuelve `typeLabel`/`iconColor`
 * y maneja los outputs.
 */
@Component({
  standalone: false,
  selector: 'core-ui-document-attachment-card',
  templateUrl: './document-attachment-card.component.html',
  styleUrls: ['./document-attachment-card.component.scss'],
  host: {
    '[class.core-ui-document-attachment-card-host--message]': "variant === 'message'",
  },
})
export class CoreUIDocumentAttachmentCardComponent {
  // A este ancho (variant="message", ~210-260px), el text-overflow:ellipsis
  // nativo de Chrome falla en pintar los puntos suspensivos para ciertos
  // nombres largos — el layout se recorta bien (overflow:hidden funciona),
  // pero el "…" no se pinta, dejando el nombre cortado a lo bruto sin
  // ningún indicador visual. Truncar acá evita depender de ese mecanismo —
  // pero el resultado tiene que entrar holgado en ~210px, si no, el propio
  // texto truncado sigue siendo lo bastante ancho como para necesitar el
  // ellipsis nativo de nuevo y cae en el mismo bug. 22 caracteres (con la
  // extensión incluida) mide bien por debajo de esa zona rota.
  private static readonly MAX_FILE_NAME_LENGTH = 22;

  @Input({ required: true }) fileName!: string;
  @Input({ required: true }) typeLabel!: string;
  @Input({ required: true }) sizeLabel!: string;
  @Input() iconColor = '#7b8795';
  @Input() variant: DocumentAttachmentCardVariant = 'default';
  @Input() state: DocumentAttachmentCardState = 'completed';
  @Input() progress: number | null = null;

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() retryClicked = new EventEmitter<void>();

  get iconSize(): 'md' | 'lg' {
    return this.variant === 'default' ? 'lg' : 'md';
  }

  get displayFileName(): string {
    const name = this.fileName ?? '';
    // El bug de Chrome (ver comentario arriba) es del ANCHO angosto del
    // card, no del viewport — un dashboard de varias columnas puede dejar
    // la columna del chat igual de angosta en desktop que en mobile (caso
    // real: layout con contactos+chat+buscador+fijados abiertos a la vez).
    // Truncar siempre para variant="message" en vez de gatear por
    // isMobileViewport(), que asumía "desktop = ancho de sobra".
    if (this.variant !== 'message' || name.length <= CoreUIDocumentAttachmentCardComponent.MAX_FILE_NAME_LENGTH) {
      return name;
    }
    const dotIndex = name.lastIndexOf('.');
    const hasExtension = dotIndex > 0 && name.length - dotIndex <= 6;
    const extension = hasExtension ? name.slice(dotIndex) : '';
    const base = hasExtension ? name.slice(0, dotIndex) : name;
    const availableForBase = CoreUIDocumentAttachmentCardComponent.MAX_FILE_NAME_LENGTH - extension.length - 1;
    return `${base.slice(0, Math.max(1, availableForBase))}…${extension}`;
  }

  get clampedProgress(): number {
    return Math.max(0, Math.min(100, this.progress ?? 0));
  }

  onCancel(): void {
    this.cancelClicked.emit();
  }

  onRetry(): void {
    this.retryClicked.emit();
  }
}
